import { loadPdfJs } from './lazylib.js';

function renderPdf(e){
    if (e.dataset.pdfBound) return;
    e.dataset.pdfBound = '1';
    const modal = e.closest('#fileviewerModal');
    const spinner = e.querySelector('[data-spinner]');
    const errorPanel = e.querySelector('[data-pdf-error]');
    const url = e.dataset.pdfUrl;
    const canvas = e.querySelector('canvas');
    let totalPages = 1;
    let currentPage = 1;
    let pdfDoc = null;
    let renderTask = null;
    let loadingTask = null;
    let loadSequence = 0;
    let renderSequence = 0;

    function reportFailure(stage, error) {
        if (window.Bugsink && typeof window.Bugsink.captureException === 'function') {
            // PDF URLs can contain access tokens; report only the stage and browser context.
            window.Bugsink.captureException(new Error(
                'PDF preview: ' + stage + ' (' + error.name + '); origin=' + window.location.origin
                + '; browser=' + navigator.userAgent
            ));
        }
    }

    function timed(promise, stage) {
        let timer;
        return Promise.race([
            promise,
            new Promise((resolve, reject) => {
                timer = setTimeout(() => {
                    const error = new Error('PDF ' + stage + ' timed out');
                    error.name = 'TimeoutError';
                    reject(error);
                }, 30000);
            })
        ]).finally(() => clearTimeout(timer));
    }

    function disposeDocument() {
        renderSequence++;
        if (renderTask) renderTask.cancel();
        renderTask = null;
        pdfDoc = null;
        if (loadingTask) {
            loadingTask.destroy().catch(error => reportFailure('cleanup', error));
            loadingTask = null;
        }
        canvas.width = 0;
        canvas.height = 0;
    }

    function showFailure(stage, error) {
        if (!e.isConnected) return;
        spinner.classList.add('hidden');
        errorPanel.hidden = false;
        disposeDocument();
        reportFailure(stage, error);
    }

    // Use different default zoom levels for desktop vs mobile
    const isMobile = window.innerWidth <= 768;
    const minZoom = isMobile ? 0.3 : 0.7;
    const maxZoom = 1;
    let zoomLevel = isMobile ? 0.3 : 0.7;
    let rotate = 0;

    function showPdf() {
        if (!pdfDoc) {
            return;
        }

        // Ensure zoom stays in sane bounds to avoid rendering issues
        if (zoomLevel < minZoom) zoomLevel = minZoom;
        if (zoomLevel > maxZoom) zoomLevel = maxZoom;

        const sequence = ++renderSequence;
        let stage = 'page load';
        if (renderTask) renderTask.cancel();
        spinner.classList.remove('hidden');
        timed(pdfDoc.getPage(currentPage), 'page load')
            .then(function (page) {
                if (sequence !== renderSequence || !e.isConnected) return;
                const viewport = page.getViewport({ scale: 2 * zoomLevel, rotation: rotate });
                const canvasWrapper = modal.querySelector("#canvasWrapper");

                // Render at device pixel ratio for sharper zoom, but keep CSS size in viewport units
                const outputScale = window.devicePixelRatio || 1;
                const cssWidth = Math.max(1, viewport.width);
                const cssHeight = Math.max(1, viewport.height);

                canvas.width = cssWidth * outputScale;
                canvas.height = cssHeight * outputScale;
                canvas.style.width = cssWidth + 'px';
                canvas.style.height = cssHeight + 'px';

                if (cssWidth > canvasWrapper.offsetWidth) {
                    canvasWrapper.classList.remove('justify-center');
                } else {
                    canvasWrapper.classList.add('justify-center');
                }

                const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;

                const renderContext = {
                    canvasContext: canvas.getContext('2d'),
                    viewport: viewport,
                    transform: transform,
                };

                stage = 'page render';
                renderTask = page.render(renderContext);
                return timed(renderTask.promise, 'page render');
            })
            .catch(function (error) {
                if (sequence === renderSequence && error.name !== 'RenderingCancelledException') {
                    showFailure(stage, error);
                }
            })
            .finally(function () {
                if (sequence === renderSequence) spinner.classList.add('hidden');
            });
    }

    function updatePageIndicator() {
        const pageIndicator = modal.querySelector("[data-page-indicator]");
        pageIndicator.textContent = `${currentPage} of ${totalPages}`;
    }

    function loadDocument() {
        const sequence = ++loadSequence;
        let stage = 'library load';
        disposeDocument();
        errorPanel.hidden = true;
        spinner.classList.remove('hidden');
        currentPage = 1;
        totalPages = 1;
        updatePageIndicator();
        loadPdfJs()
            .then(function (pdfjs) {
                if (sequence !== loadSequence || !e.isConnected) return null;
                stage = 'document load';
                loadingTask = pdfjs.getDocument(url);
                return timed(loadingTask.promise, stage);
            })
            .then(function (doc) {
                if (!doc || sequence !== loadSequence || !e.isConnected) return;
                pdfDoc = doc;
                totalPages = doc.numPages;
                updatePageIndicator();
                showPdf();
            })
            .catch(function (error) {
                if (sequence === loadSequence) showFailure(stage, error);
            });
    }

    e.querySelector('[data-pdf-retry]').addEventListener('click', loadDocument);
    const observer = new MutationObserver(() => {
        if (!e.isConnected) {
            loadSequence++;
            disposeDocument();
            observer.disconnect();
        }
    });
    observer.observe(modal.parentNode, { childList: true, subtree: true });
    loadDocument();

    modal.querySelector('[data-prev-page]').addEventListener('click', () => {
        if (currentPage > 1) {
            currentPage--;
            showPdf();
            updatePageIndicator();
        }
    });
    
    modal.querySelector('[data-next-page]').addEventListener('click', () => {
        if (currentPage < totalPages) {
            currentPage++;
            showPdf();
            updatePageIndicator();
        }
    });
    
    modal.querySelector("[data-zoom-in]").addEventListener('click', () => {
        if (zoomLevel < maxZoom) {
            zoomLevel = Math.min(maxZoom, zoomLevel + 0.2);
        }
        showPdf();
    });
    
    modal.querySelector("[data-zoom-out]").addEventListener('click', () => {
        if (zoomLevel > minZoom) {
            zoomLevel = Math.max(minZoom, zoomLevel - 0.2);
        }
        showPdf();
    });

    // Pinch-to-zoom support on touch devices
    (function initPinchToZoom() {
        const canvasWrapper = modal.querySelector("#canvasWrapper");
        if (!canvasWrapper) return;

        let initialDistance = null;
        let initialZoomLevel = null;
        let pinchRafId = null;

        function getDistance(touches) {
            const dx = touches[0].clientX - touches[1].clientX;
            const dy = touches[0].clientY - touches[1].clientY;
            return Math.sqrt(dx * dx + dy * dy);
        }

        canvasWrapper.addEventListener(
            'touchstart',
            (event) => {
                if (event.touches.length === 2) {
                    initialDistance = getDistance(event.touches);
                    // Guard against zero / invalid distance to avoid wild zoom jumps
                    if (!initialDistance || !isFinite(initialDistance)) {
                        initialDistance = null;
                        initialZoomLevel = null;
                        return;
                    }
                    initialZoomLevel = zoomLevel;
                } else {
                    initialDistance = null;
                    initialZoomLevel = null;
                }
            },
            { passive: true }
        );

        canvasWrapper.addEventListener(
            'touchmove',
            (event) => {
                if (event.touches.length === 2 && initialDistance && initialZoomLevel !== null) {
                    event.preventDefault();

                    const currentDistance = getDistance(event.touches);
                    const scaleChange = currentDistance && isFinite(currentDistance)
                        ? currentDistance / initialDistance
                        : 1;

                    // Apply scale change relative to starting zoom level
                    let newZoom = initialZoomLevel * scaleChange;

                    // Clamp to same bounds as buttons
                    if (newZoom < minZoom) newZoom = minZoom;
                    if (newZoom > maxZoom) newZoom = maxZoom;

                    zoomLevel = newZoom;

                    // Throttle re-render to animation frames to avoid visual tearing
                    if (!pinchRafId) {
                        pinchRafId = requestAnimationFrame(() => {
                            pinchRafId = null;
                            showPdf();
                        });
                    }
                }
            },
            { passive: false }
        );

        canvasWrapper.addEventListener(
            'touchend',
            () => {
                // Reset pinch state when fingers are lifted to avoid stale values
                initialDistance = null;
                initialZoomLevel = null;
            },
            { passive: true }
        );
    })();

    modal.querySelector('[data-rotate]').addEventListener('click', () => {
        rotate = (rotate + 90) % 360;
        showPdf();
    });
};

function shareFile(e){
    function showError(){
        const errorElement = document.getElementById("error-message")
        errorElement.classList.remove('hidden');
        setTimeout(() => {
            errorElement.classList.add('hidden');
        },5000)
    }
    e.addEventListener('click', () => {
        const url = e.dataset.fileUrl;
        const extension = e.dataset.ext;

        if (!navigator.share) {
            showError();
            return;
        }
        fetch(url)
        .then((response) => {
            return response.blob();
        })
        .then((blob) => {
            let mimeType = 'application/pdf';
            if(extension === 'jpg' || extension === 'jpeg') mimeType = 'image/jpeg';
            else if(extension === 'png') mimeType = 'image/png';

            const fileName = url.split('/').pop();
            const fileBlob = new Blob([blob], { type: mimeType });
            navigator.share({
                files: [
                    new File([fileBlob], fileName, { type: mimeType })
                ]
            })
        })
        .catch((error) => {
            showError()
        });
    })
}

(() => {
    window.renderPdf = renderPdf;
    window.shareFile = shareFile;
})()